import { useState } from 'react';
import type { Lead } from '@shared/types';
import { stageStyle } from '@shared/stageColors';
import { useSetStage, useStages } from '../store';
import { Popover } from './ui';

export function PillLabel({ name, className, style }: { name: string; className?: string; style?: React.CSSProperties }) {
  const s = stageStyle(name);
  return <span className={`pill ${className ?? ''}`} style={{ background: s.bg, borderColor: s.border, color: s.text, cursor: 'default', ...style }}>{name}</span>;
}

/**
 * The status pill: click opens a stage picker (popover on desktop, sheet on touch); choosing a stage writes to the CRM.
 * `onChanged` runs after a new stage is picked — on the board it opens the lead, so a lead that leaves the current
 * tab/column stays in view and a note can be added right away.
 */
export function StagePill({ lead, size = 'sm', variant = 'default', onChanged }: { lead: Lead; size?: 'sm' | 'md'; variant?: 'default' | 'card'; onChanged?: () => void }) {
  const { stageName, meta } = useStages();
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const setStage = useSetStage();
  const name = stageName(lead.stageId);
  const s = stageStyle(name);
  const pipeline = meta?.pipelines.find((p) => p.id === lead.pipelineId);
  return (
    <>
      <button
        ref={setAnchor}
        type="button"
        className={variant === 'card'
          // the lead card's 3rd line: same 12px as its text; ::before pads the tap target to 44px without moving anything
          // may shrink with an ellipsis when its row is tight (the card gives the name priority); the title keeps the full name
          ? `relative inline-flex max-w-full min-w-0 items-center justify-center gap-1 rounded-md px-2.5 py-0.5 text-[12px] font-bold leading-[18px] whitespace-nowrap transition-transform duration-150 hover:scale-105 before:absolute before:inset-x-0 before:-inset-y-3 before:content-[''] ${s.family === 'tinted' ? 'border' : ''}`
          : 'pill'}
        style={variant === 'card' ? { background: s.bg, borderColor: s.border, color: s.text } : { background: s.bg, borderColor: s.border, color: s.text, fontSize: size === 'md' ? 13 : 12 }}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={`סטטוס: ${name} — לחץ לשינוי`}
      >
        {variant === 'card' ? <span className="min-w-0 truncate">{name}</span> : name}
        {setStage.isPending && setStage.variables?.id === lead.id ? <span className="opacity-70">…</span> : null}
      </button>
      <StagePicker open={open} anchor={anchor} onClose={() => setOpen(false)} stages={pipeline?.stages.map((x) => x.name) ?? []} current={name}
        onPick={(stName) => { setOpen(false); const st = pipeline?.stages.find((x) => x.name === stName); if (st && st.id !== lead.stageId) { setStage.mutate({ id: lead.id, stageId: st.id }); onChanged?.(); } }} />
    </>
  );
}

/** A 3-column grid of pills (2 on phones) so the whole vocabulary is visible without scrolling. */
export function StagePicker({ open, anchor, onClose, stages, current, onPick, allowClear }: { open: boolean; anchor: HTMLElement | null; onClose: () => void; stages: string[]; current?: string; onPick: (stageName: string) => void; allowClear?: string }) {
  return (
    <Popover open={open} anchor={anchor} onClose={onClose} title="בחר סטטוס">
      <div role="listbox" className="grid grid-cols-2 gap-2 p-1 desk:w-[360px] desk:grid-cols-3" onClick={(e) => e.stopPropagation()}>
        {allowClear !== undefined && (
          <button type="button" role="option" aria-selected={!current} className={`pill justify-center border-line bg-panel text-ink-2 ${!current ? 'ring-2 ring-accent' : ''}`} style={{ minHeight: 36, whiteSpace: 'normal', textAlign: 'center' }} onClick={() => onPick('')}>{allowClear}</button>
        )}
        {stages.map((name) => {
          const s = stageStyle(name);
          const active = name === current;
          return (
            <button key={name} type="button" role="option" aria-selected={active} className={`pill justify-center ${active ? 'ring-2 ring-offset-1 ring-accent' : ''}`}
              style={{ background: s.bg, borderColor: s.border, color: s.text, minHeight: 36, whiteSpace: 'normal', textAlign: 'center', lineHeight: 1.2 }} onClick={() => onPick(name)}>
              {name}
            </button>
          );
        })}
      </div>
    </Popover>
  );
}

/** Toolbar status filter: a button that looks like the chosen pill and opens the same coloured grid. */
export function StageFilterDropdown({ value, stages, onChange }: { value: string; stages: string[]; onChange: (name: string) => void }) {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const s = value ? stageStyle(value) : null;
  return (
    <>
      <button ref={setAnchor} type="button" className="input flex w-44 items-center justify-between gap-2 text-start" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(true)}>
        {s ? <span className="pill" style={{ background: s.bg, borderColor: s.border, color: s.text, cursor: 'pointer' }}>{value}</span> : <span>כל הסטטוסים</span>}
        <span className="text-ink-3">▾</span>
      </button>
      <StagePicker open={open} anchor={anchor} onClose={() => setOpen(false)} stages={stages} current={value || undefined} allowClear="כל הסטטוסים" onPick={(n) => { setOpen(false); onChange(n); }} />
    </>
  );
}
